<template>
  <div v-if="visible" class="settings-dropdown-menu" @click.stop>
    <!-- 标题 -->
    <div class="settings-dropdown-header">
      <h3>设置</h3>
    </div>
    
    <!-- 设置内容 -->
    <div class="settings-dropdown-content">
      <!-- 语言选择 -->
      <div class="settings-section">
        <div class="settings-label">语言选择</div>
        <div class="settings-options">
          <button
            v-for="lang in languages"
            :key="lang"
            class="settings-option-btn"
            :class="{ active: settings.language === lang }"
            @click="updateSetting('language', lang)"
          >
            {{ lang }}
          </button>
        </div>
      </div>

      <!-- 显示排名 -->
      <div class="settings-section">
        <div class="settings-label">显示排名</div>
        <div class="settings-options">
          <button
            v-for="option in rankingOptions"
            :key="option.value"
            class="settings-option-btn"
            :class="{ active: settings.showRanking === option.value }"
            @click="updateSetting('showRanking', option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <!-- 排序规则 -->
      <div class="settings-section">
        <div class="settings-label">排序规则</div>
        <div class="settings-options">
          <button
            v-for="sort in sortRules"
            :key="sort.value"
            class="settings-option-btn"
            :class="{ active: settings.sortRule === sort.value }"
            @click="updateSetting('sortRule', sort.value)"
          >
            {{ sort.label }}
          </button>
        </div>
      </div>

      <!-- 提示范围 -->
      <div class="settings-section">
        <div class="settings-label">提示范围</div>
        <div class="settings-options">
          <button
            v-for="scope in tipScopes"
            :key="scope.value"
            class="settings-option-btn"
            :class="{ active: settings.tipScope === scope.value }"
            @click="updateSetting('tipScope', scope.value)"
          >
            {{ scope.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, watch } from 'vue'

const props = defineProps({
  /**
   * 控制下拉菜单的显示/隐藏
   */
  visible: {
    type: Boolean,
    default: false
  },
  /**
   * 可以接收初始设置
   */
  initialSettings: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:visible', 'settings-change'])

// 语言选项
const languages = ['简体', '繁体', 'English']

// 显示排名选项
const rankingOptions = [
  { label: '开启', value: true },
  { label: '关闭', value: false }
]

// 排序规则
const sortRules = [
  { label: '按时间', value: 'time' },
  { label: '按联赛', value: 'league' }
]

// 提示范围选项（仅两个选项）
const tipScopes = [
  { label: '收藏', value: 'favorite' },
  { label: '关闭', value: 'close' }
]

// 设置状态
const settings = reactive({
  language: '简体',
  showRanking: true,
  sortRule: 'time',
  tipScope: 'favorite'
})

/**
 * 更新设置项
 * @param {string} key - 设置项的键名
 * @param {any} value - 设置项的新值
 */
const updateSetting = (key, value) => {
  settings[key] = value
  emitSettingsChange()
}

/**
 * 发送设置变更事件
 */
const emitSettingsChange = () => {
  emit('settings-change', { ...settings })
  // 保存到 localStorage
  saveSettings()
}

/**
 * 保存设置到 localStorage
 */
const saveSettings = () => {
  try {
    localStorage.setItem('basketball-score-settings', JSON.stringify(settings))
  } catch (error) {
    console.error('保存设置失败:', error)
  }
}

/**
 * 从 localStorage 加载设置
 */
const loadSettings = () => {
  try {
    const saved = localStorage.getItem('basketball-score-settings')
    if (saved) {
      const parsedSettings = JSON.parse(saved)
      Object.assign(settings, parsedSettings)
    }
  } catch (error) {
    console.error('加载设置失败:', error)
  }
}

/**
 * 初始化设置
 */
const initSettings = () => {
  // 优先从 localStorage 加载
  loadSettings()
  
  // 如果有传入初始设置，则覆盖
  if (props.initialSettings && Object.keys(props.initialSettings).length > 0) {
    Object.assign(settings, props.initialSettings)
  }
}

// 初始化
initSettings()

// 监听 initialSettings 变化
watch(
  () => props.initialSettings,
  (newSettings) => {
    if (newSettings && Object.keys(newSettings).length > 0) {
      Object.assign(settings, newSettings)
    }
  },
  { deep: true }
)
</script>

<style scoped lang="scss">
.settings-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  width: 360px;
  background: $bg-white;
  border-radius: 10px;
  border: 1px solid $border-color;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  // 标题
  .settings-dropdown-header {
    padding: 10px 14px;
    background: rgba($color-primary, 0.08);
    border-radius: 10px 10px 0 0;

    h3 {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      color: $text-dark;
      text-align: center;
    }
  }

  // 内容区域
  .settings-dropdown-content {
    padding: 12px 14px;
    max-height: none;
    overflow: visible;

    .settings-section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
      padding-bottom: 10px;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;
      }

      .settings-label {
        font-size: 13px;
        font-weight: 600;
        color: $text-dark;
        min-width: 70px;
        flex-shrink: 0;
      }

      .settings-options {
        display: flex;
        gap: 6px;
        flex-wrap: nowrap;
        align-items: center;

        .settings-option-btn {
          padding: 5px 14px;
          font-size: 12px;
          font-weight: 500;
          color: $text-secondary;
          background: #f5f7fa;
          border: 1px solid transparent;
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 58px;
          text-align: center;
          white-space: nowrap;

          &:hover {
            background: $bg-hover;
            color: $color-primary;
            border-color: rgba($color-primary, 0.3);
          }

          &.active {
            background: linear-gradient(135deg, $color-primary 0%, $color-primary-dark 100%);
            color: $bg-white;
            border-color: transparent;
            font-weight: 600;

            &:hover {
              background: linear-gradient(135deg, $color-primary-dark 0%, darken($color-primary-dark, 8%) 100%);
            }
          }
        }
      }
    }
  }
}
</style>
